class ReviewCarousel extends window.HTMLElement {
    constructor() {
        super()
        this.selectors = {
            loop: "data-loop",
            scrollCount: "[data-scroll-num]",
            dragFree: "data-drag-free",
            align: "data-align",
            controls: "data-controls",
            autoplay: "data-autoplay",
            autoplayInterval: "data-autoplay-interval",
            carouselContainer: ".carousel-container",
            progressBar: ".progress-bar",
            carouselText: ".carousel-text",
            carouselTextContainer: ".carousel-text-container",
            indicatorIcon: ".indicator-icon",
        }

        this.autoplayPlugin = null
        this.observer = null
        this.autoplayInterval = null
    }

    connectedCallback() {
        const loop = this.hasAttribute(this.selectors.loop)
        const scrollCount = this.getAttribute(this.selectors.scrollCount) ?? 1
        const dragFree = this.hasAttribute(this.selectors.dragFree)
        const controls = this.hasAttribute(this.selectors.controls)
        const align = this.getAttribute(this.selectors.align) ?? "start"
        const autoplay = this.hasAttribute(this.selectors.autoplay)
        const autoplayInterval =
            parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000

        const carouselContainer = this.querySelector(
            this.selectors.carouselContainer
        )

        const plugins = []

        if (autoplay) {
            this.autoplayPlugin = EmblaCarouselAutoplay({
                delay: autoplayInterval,
            })
            plugins.push(this.autoplayPlugin)
        }

        this.embla = EmblaCarousel(
            carouselContainer,
            {
                loop: loop,
                align: align,
                dragFree: dragFree,
                skipSnaps: true,
                slidesToScroll: parseInt(scrollCount),
            },
            plugins
        )

        if (controls) {
            this.setupControls()
        }

        this.setupDots()
        this.setupTextClickHandlers()

        if (autoplay) {
            this.setupIntersectionObserver()
            this.resetProgressBar()
            this.startProgressBar()
        }

        this.embla.on("select", () => {
            this.resetProgressBar()
            this.startProgressBar()
            this.resetAutoplay()
            this.updateTextOpacity()
        })

        this.updateTextOpacity()
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = null
        }
        if (this.embla) {
            this.embla.destroy()
            this.embla = null
        }
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval)
            this.autoplayInterval = null
        }
    }

    setupControls() {
        const prevBtnNodes = Array.from(
            this.querySelectorAll(".embla__button--prev")
        )
        const nextBtnNodes = Array.from(
            this.querySelectorAll(".embla__button--next")
        )

        prevBtnNodes.forEach((prevBtnNode, index) => {
            const nextBtnNode = nextBtnNodes[index]
            if (nextBtnNode) {
                const removePrevNextBtnsClickHandlers =
                    this.addPrevNextBtnsClickHandlers(
                        this.embla,
                        prevBtnNode,
                        nextBtnNode
                    )
                this.embla.on("destroy", removePrevNextBtnsClickHandlers)
            }
        })
    }

    setupDots() {
        const dots = Array.from(this.querySelectorAll(".embla__dots"))
        dots.forEach((dot, index) => {
            const removeDotBtnsAndClickHandlers =
                this.addDotBtnsAndClickHandlers(this.embla, dot)
            this.embla.on("destroy", removeDotBtnsAndClickHandlers)
        })
    }

    setupIntersectionObserver() {
        const options = {
            root: null,
            threshold: 0.1,
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.autoplayPlugin.play()
                    this.startProgressBar()
                } else {
                    this.autoplayPlugin.stop()
                    this.resetProgressBar()
                }
            })
        }, options)

        this.observer.observe(this)
    }

    resetAutoplay() {
        if (this.autoplayPlugin) {
            this.autoplayPlugin.stop()
            this.autoplayPlugin.play()
        }
    }

    startProgressBar() {
        const progressBars = this.querySelectorAll(this.selectors.progressBar)
        if (progressBars.length === 0) return // Check if progress bars exist

        const autoplayInterval =
            parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000
        progressBars.forEach((progressBar) => {
            requestAnimationFrame(() => {
                progressBar.style.transition = `width ${autoplayInterval}ms linear`
                progressBar.style.width = "100%"
            })
        })
    }

    resetProgressBar() {
        const progressBars = this.querySelectorAll(this.selectors.progressBar)
        if (progressBars.length === 0) return // Check if progress bars exist

        progressBars.forEach((progressBar) => {
            progressBar.style.transition = "none"
            progressBar.style.width = "0%"
        })
    }

    addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn) {
        const togglePrevNextBtnsState = () => {
            if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled")
            else prevBtn.setAttribute("disabled", "disabled")

            if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled")
            else nextBtn.setAttribute("disabled", "disabled")
        }

        emblaApi
            .on("select", togglePrevNextBtnsState)
            .on("init", togglePrevNextBtnsState)
            .on("reInit", togglePrevNextBtnsState)

        return () => {
            prevBtn.removeAttribute("disabled")
            nextBtn.removeAttribute("disabled")
        }
    }

    addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
        const scrollPrev = () => {
            emblaApi.scrollPrev()
        }
        const scrollNext = () => {
            emblaApi.scrollNext()
        }
        prevBtn.addEventListener("click", scrollPrev, false)
        nextBtn.addEventListener("click", scrollNext, false)

        const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
            emblaApi,
            prevBtn,
            nextBtn
        )

        return () => {
            removeTogglePrevNextBtnsActive()
            prevBtn.removeEventListener("click", scrollPrev, false)
            nextBtn.removeEventListener("click", scrollNext, false)
        }
    }
    addDotBtnsAndClickHandlers(emblaApi, dotsNode) {
        let dotNodes = []

        const addDotBtnsWithClickHandlers = () => {
            dotsNode.innerHTML = emblaApi
                .scrollSnapList()
                .map(() => '<button class="embla__dot" type="button"></button>')
                .join("")

            const scrollTo = (index) => {
                emblaApi.scrollTo(index)
            }

            dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"))
            dotNodes.forEach((dotNode, index) => {
                dotNode.addEventListener("click", () => scrollTo(index), false)
            })
        }

        const toggleDotBtnsActive = () => {
            const previous = emblaApi.previousScrollSnap()
            const selected = emblaApi.selectedScrollSnap()
            dotNodes[previous].classList.remove("embla__dot--selected")
            dotNodes[selected].classList.add("embla__dot--selected")
        }

        emblaApi
            .on("init", addDotBtnsWithClickHandlers)
            .on("reInit", addDotBtnsWithClickHandlers)
            .on("init", toggleDotBtnsActive)
            .on("reInit", toggleDotBtnsActive)
            .on("select", toggleDotBtnsActive)

        return () => {
            dotsNode.innerHTML = ""
        }
    }

    setupTextClickHandlers() {
        const textContainers = this.querySelectorAll(
            this.selectors.carouselTextContainer
        )
        textContainers.forEach((container) => {
            container.addEventListener("click", () => {
                const index = parseInt(container.getAttribute("data-index"))
                this.embla.scrollTo(index)
            })
        })
    }

    updateTextOpacity() {
        const selectedIndex = this.embla.selectedScrollSnap()
        const textContainers = this.querySelectorAll(
            this.selectors.carouselTextContainer
        )

        textContainers.forEach((container, index) => {
            const textElement = container.querySelector(
                this.selectors.carouselText
            )
            const indicatorIcon = container.querySelector(
                this.selectors.indicatorIcon
            )

            if (index === selectedIndex) {
                textElement.style.opacity = "1"
                indicatorIcon.classList.remove("invisible")
            } else {
                textElement.style.opacity = "0.5"
                indicatorIcon.classList.add("invisible")
            }
        })
    }
}

window.customElements.define("review-carousel", ReviewCarousel)
